import React, { useEffect } from 'react';
import { Box, Slider } from '@mui/material';
import Grid from '../../../../Geodata/grid';
import { VisualizationElements } from '../../../../Graphics/VisualizationElements';
import { ControllerManager } from '../../../controller/ControllerManager';
import type { SensorData } from 'Simulations/SensorPoint';

// Zustand store
import usePersistentStore from '../../../store/persistent/persistentStore';

// Highlight hook
import { useAutoHighlight, highlightSx } from '../../generic/useAutoHighlight';

// Density block helper
import { getDensityRenderScale } from 'data/Simulations/pipeline/blocks/DensityBlock';

// --------------- Helper -----------------------------------------------
// snap slider values onto the valid grid step and clamp to bounds.
function snapClamp(val: number, step: number, min: number, max: number) {
    const snapped = Math.round((val - min) / step) * step + min;
    return Math.min(max, Math.max(min, snapped));
}

// Clamp a UI slider index to a valid range in Z, preventing out-of-bounds errors when the  grid changes.
function clampIndex(idx: number, length: number) {
    if (length <= 0) return 0;
    return Math.max(0, Math.min(length - 1, idx));
}

// In 1-to-3 density, valid Z positions are scattered;
// when the selector tries to stay on the "same Z", it maps to the closest active Z for that (x,y).
function findClosestIndex(sorted: number[], value: number): number {
    if (!sorted.length) return -1;
    let lo = 0;
    let hi = sorted.length - 1;
    while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        const midVal = sorted[mid];
        if (midVal === value) return mid;
        if (midVal < value) lo = mid + 1;
        else hi = mid - 1;
    }
    // lo is insertion point
    if (lo <= 0) return 0;
    if (lo >= sorted.length) return sorted.length - 1;
    const prev = sorted[lo - 1];
    const next = sorted[lo];
    return Math.abs(value - prev) <= Math.abs(next - value) ? lo - 1 : lo;
}

// --------------- SensorSelection --------------------------------------

type SensorSelectionProps = {};

const SensorSelection: React.FC<SensorSelectionProps> = () => {
    const pipelineExecutionCount = usePersistentStore((s) => s.getActiveLayerState()?.pipeline?.executionCount);
    const { simulationItems, element } = usePersistentStore((s) => s.getActiveLayerState());
    const updateLayerState = usePersistentStore().updateCurrentLayerState;
    const { x, y, z } = simulationItems;

    // Read current element mode (Normal, 1 to 3, 1 ev 3)
    const densityScale = getDensityRenderScale(element?.ELEMENT_CURRENT_DENSITY);

    //  Needed for snapping to an active point when density is enabled.
    const prePhaseSensorIndices = React.useMemo(() => {
        const preSensors = ControllerManager.getInstance().getPipelineController().getLastPreResult();
        if (!preSensors?.length) return [];
        return preSensors.map((sd: SensorData) => sd.getIndex());
    }, [pipelineExecutionCount]);

    // For density 1-to-3,  for every (x,y) it stores which z values are used.
    const activeZByXY = React.useMemo(() => {
        if (densityScale !== 3) return null;
        if (!prePhaseSensorIndices.length) return null;

        const map = new Map<string, number[]>();
        const push = (xv: number, yv: number, zv: number) => {
            const key = `${xv},${yv}`;
            const list = map.get(key);
            if (list) list.push(zv);
            else map.set(key, [zv]);
        };

        for (const idx of prePhaseSensorIndices) {
            const [xv, yv, zv] = Grid.indexToXYZ(idx);
            push(xv, yv, zv);
        }

        if (!map.size) return null;
        for (const list of map.values()) list.sort((a, b) => a - b);
        return map;
    }, [densityScale, prePhaseSensorIndices]);

    // Look up helper
    const getActiveZList = React.useCallback(
        (xv: number, yv: number) => {
            if (!activeZByXY) return null;
            return activeZByXY.get(`${xv},${yv}`) ?? null;
        },
        [activeZByXY]
    );

    // Build axis converter (UI - Grid) based on mode and active points
    const { xAxis, yAxis, zAxis } = React.useMemo(() => {
        const buildAxis = (maxIndex: number) => {
            if (densityScale !== 3) {
                return {
                    gridStep: 1,
                    gridMin: 0,
                    gridMax: maxIndex,
                    uiMin: 0,
                    uiMax: maxIndex,
                    toUi: (gridValue: number) => gridValue,
                    toGrid: (uiValue: number) => uiValue,
                    usesList: false,
                };
            }

            const gridMin = 1;
            const gridMax = maxIndex - ((maxIndex - gridMin) % densityScale);
            const count = Math.floor((gridMax - gridMin) / densityScale) + 1;

            return {
                gridStep: densityScale,
                gridMin,
                gridMax,
                uiMin: 1,
                uiMax: count,
                toUi: (gridValue: number) => Math.round((gridValue - gridMin) / densityScale) + 1,
                toGrid: (uiValue: number) => gridMin + (uiValue - 1) * densityScale,
                usesList: false,
            };
        };

        const buildZAxisFromList = (list: number[]) => {
            return {
                gridStep: 1,
                gridMin: list[0],
                gridMax: list[list.length - 1],
                uiMin: 1,
                uiMax: list.length,
                toUi: (gridValue: number) => findClosestIndex(list, gridValue) + 1,
                toGrid: (uiValue: number) => list[clampIndex(Math.round(uiValue) - 1, list.length)],
                usesList: true,
                list,
            };
        };
        //Safety check when pipeline is not executed yet or returns no sensors: fallback to normal axis.
        if (densityScale === 3) {
            const zList = getActiveZList(x, y);
            if (!zList?.length) {
                return {
                    xAxis: buildAxis(Grid.gridX - 1),
                    yAxis: buildAxis(Grid.gridY - 1),
                    zAxis: buildAxis(0),
                };
            }
            return {
                xAxis: buildAxis(Grid.gridX - 1),
                yAxis: buildAxis(Grid.gridY - 1),
                zAxis: buildZAxisFromList(zList),
            };
        }

        return {
            xAxis: buildAxis(Grid.gridX - 1),
            yAxis: buildAxis(Grid.gridY - 1),
            zAxis: buildAxis(Grid.gridZ - 1),
        };
    }, [densityScale, getActiveZList, x, y]);

    // Sliders display the aggregated index even if the underlying grid index jumps by 3.
    const uiX = xAxis.toUi(x);
    const uiY = yAxis.toUi(y);
    const uiZ = zAxis.toUi(z);

    const setXYZ = React.useCallback(
        (nx: number, ny: number, nz: number) => {
            updateLayerState({ simulationItems: { ...simulationItems, x: nx, y: ny, z: nz } });
        },
        [simulationItems, updateLayerState]
    );

    const snapZForXY = React.useCallback(
        (xv: number, yv: number, targetZ: number) => {
            if (densityScale !== 3) return snapClamp(targetZ, zAxis.gridStep, zAxis.gridMin, zAxis.gridMax);
            const list = getActiveZList(xv, yv);
            if (!list?.length) return targetZ;
            const idx = findClosestIndex(list, targetZ);
            return list[clampIndex(idx, list.length)];
        },
        [densityScale, getActiveZList, zAxis.gridStep, zAxis.gridMin, zAxis.gridMax]
    );

    const handleChange = (key: 'x' | 'y' | 'z', value: number | number[]) => {
        const v = Array.isArray(value) ? value[0] : value;
        let nx = x;
        let ny = y;
        let nz = z;

        if (key === 'x') nx = snapClamp(xAxis.toGrid(v), xAxis.gridStep, xAxis.gridMin, xAxis.gridMax);
        if (key === 'y') ny = snapClamp(yAxis.toGrid(v), yAxis.gridStep, yAxis.gridMin, yAxis.gridMax);
        if (key === 'z') {
            const nextZ = zAxis.toGrid(v);
            nz = zAxis.usesList ? nextZ : snapClamp(nextZ, zAxis.gridStep, zAxis.gridMin, zAxis.gridMax);
        }

        if (densityScale === 3 && (key === 'x' || key === 'y')) {
            const list = getActiveZList(nx, ny);
            if (list?.length) {
                const desiredUiZ = zAxis.toUi(z);
                nz = list[clampIndex(Math.round(desiredUiZ) - 1, list.length)];
            }
        }

        if (nx !== x || ny !== y || nz !== z) setXYZ(nx, ny, nz);
    };

    // If mode changes, align existing values to valid step grid.
    useEffect(() => {
        let nx = snapClamp(x, xAxis.gridStep, xAxis.gridMin, xAxis.gridMax);
        let ny = snapClamp(y, yAxis.gridStep, yAxis.gridMin, yAxis.gridMax);
        let nz = zAxis.usesList ? zAxis.toGrid(zAxis.toUi(z)) : snapClamp(z, zAxis.gridStep, zAxis.gridMin, zAxis.gridMax);
        if (densityScale === 3) nz = snapZForXY(nx, ny, nz);
        if (nx !== x || ny !== y || nz !== z) setXYZ(nx, ny, nz);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [xAxis, yAxis, zAxis, densityScale, snapZForXY]);

    // Syncs visual selector with the sensor position.
    useEffect(() => {
        const idx = Grid.xyzToIndex(x, y, z);
        const sensorPoint = Grid.sensorPoints?.[idx];
        const position = sensorPoint?.getCenter?.();
        if (position) VisualizationElements.moveSelectedBorder(position, densityScale);
    }, [x, y, z, densityScale]);

    // Auto-highlight logic for sliders
    const ahX = useAutoHighlight<number, HTMLElement>(uiX);
    const ahY = useAutoHighlight<number, HTMLElement>(uiY);
    const ahZ = useAutoHighlight<number, HTMLElement>(uiZ);

    const renderAxisSlider = (
        key: 'x' | 'y' | 'z',
        label: string,
        axis: { uiMin: number; uiMax: number },
        value: number,
        ah: { ref: (node: HTMLElement | null) => void; markManual: () => void }
    ) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box style={{ marginRight: '10px' }}>{label}</Box>
            <Slider
                ref={ah.ref}
                sx={[highlightSx(), { flex: 1 }]}
                className="panel-slider"
                size="small"
                value={value}
                onChange={(_, v) => handleChange(key, v)}
                step={1}
                min={axis.uiMin}
                max={axis.uiMax}
                valueLabelDisplay="on"
                onPointerDownCapture={ah.markManual}
                onKeyDownCapture={ah.markManual}
            />
        </Box>
    );

    return (
        <Box sx={{ pr: 1.2 }}>
            {renderAxisSlider('x', 'X', xAxis, uiX, ahX)}
            {renderAxisSlider('y', 'Y', yAxis, uiY, ahY)}
            {renderAxisSlider('z', 'Z', zAxis, uiZ, ahZ)}
        </Box>
    );
};

export default SensorSelection;
